import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { useLocationCtx } from "@/context/LocationContext";
import { useAuth } from "@/context/AuthContext";
import { discountLabel, distanceLabel } from "@/lib/format";
import { List, MapPin, Loader2, Store, Plus, Minus, LocateFixed, Heart, Clock, Star, Navigation, X, Search, Store as StoreIcon, ChevronDown } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";

const GKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const MIN_Z = 10, MAX_Z = 18;

function project(lat, lng) {
  const siny = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
  return { x: 256 * (0.5 + lng / 360), y: 256 * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)) };
}
function worldToLatLng(wx, wy) {
  const lng = (wx / 256 - 0.5) * 360;
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(Math.PI * (1 - 2 * (wy / 256))));
  return { lat, lng };
}
function isOpenNow(store) {
  const hs = store?.hours_struct;
  if (!Array.isArray(hs) || hs.length === 0) return true; // unknown -> keep visible
  const now = new Date();
  const day = hs[(now.getDay() + 6) % 7];
  if (!day || day.closed || !(day.ranges && day.ranges.length)) return false;
  const cur = now.toTimeString().slice(0, 5);
  return day.ranges.some((r) => r.from && r.to && r.from <= cur && cur <= r.to);
}
function todayHours(store) {
  const hs = store?.hours_struct;
  if (!Array.isArray(hs) || hs.length === 0) return store?.hours ? store.hours.split("\n")[0] : "Orari non disponibili";
  const day = hs[(new Date().getDay() + 6) % 7];
  if (!day || day.closed || !(day.ranges && day.ranges.length)) return "Chiuso oggi";
  return "Oggi " + day.ranges.map((r) => `${r.from}–${r.to}`).join(", ");
}

export default function OffersMap() {
  const { loc, requestGeo, locating } = useLocationCtx();
  const { openLocation } = useOutletContext() || {};
  const { client } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [preview, setPreview] = useState(null);
  const [zoom, setZoom] = useState(13);
  const [cats, setCats] = useState([]);
  const [selCats, setSelCats] = useState([]);
  const [expandedMacro, setExpandedMacro] = useState(null);
  const [openOnly, setOpenOnly] = useState(false);
  const [favOnly, setFavOnly] = useState(false);
  const [mapQ, setMapQ] = useState("");
  const [center, setCenter] = useState({ lat: loc.lat, lng: loc.lng });
  const [delta, setDelta] = useState({ x: 0, y: 0 });
  const deltaRef = useRef({ x: 0, y: 0 });
  const dragRef2 = useRef({ active: false, sx: 0, sy: 0 });
  const [stores, setStores] = useState({});
  const boxRef = useRef(null);
  const [box, setBox] = useState({ w: 640, h: 640 });
  const [pinch, setPinch] = useState(1);
  const pinchRef = useRef({ active: false, startDist: 0, startZoom: 13, scale: 1 });

  const following = new Set((client && client.following) || []);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const selCatIds = selCats;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { lat: loc.lat, lng: loc.lng, radius: loc.radius };
      if (selCatIds.length) params.categories = selCatIds.join(",");
      const r = await api.get("/offers/today", { params });
      const list = (r.data || []).filter((o) => o.lat != null && o.lng != null);
      setOffers(list);
      setActive(null); setPreview(null);
      setLoading(false);
      // background: fetch store details (hours/logo/rating) for open-now + preview
      const ids = [...new Set(list.map((o) => o.vendor_id))].slice(0, 25).filter((id) => !stores[id]);
      if (ids.length) {
        const res = await Promise.all(ids.map((id) => api.get(`/stores/${id}`).then((x) => x.data.store).catch(() => null)));
        setStores((prev) => { const n = { ...prev }; res.forEach((s) => { if (s) n[s.vendor_id] = s; }); return n; });
      }
    } catch (_) { setLoading(false); }
    // eslint-disable-next-line
  }, [loc.lat, loc.lng, loc.radius, JSON.stringify(selCats), cats.length]);

  useEffect(() => { load(); }, [load]);

  // keep map center synced to the client's location (recenter / geo)
  useEffect(() => { setCenter({ lat: loc.lat, lng: loc.lng }); }, [loc.lat, loc.lng]);

  // drag-to-pan: translate live, on release recompute center + refetch Google tile
  useEffect(() => {
    const onMove = (e) => {
      if (pinchRef.current.active && e.touches && e.touches.length === 2) {
        const a = e.touches[0], b = e.touches[1];
        const d = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        const s = Math.min(2.4, Math.max(0.42, d / pinchRef.current.startDist));
        pinchRef.current.scale = s;
        setPinch(s);
        if (e.cancelable) e.preventDefault();
        return;
      }
      if (!dragRef2.current.active) return;
      const pt = e.touches ? e.touches[0] : e;
      const d = { x: pt.clientX - dragRef2.current.sx, y: pt.clientY - dragRef2.current.sy };
      deltaRef.current = d; setDelta(d);
    };
    const onUp = () => {
      if (pinchRef.current.active) {
        const newZoom = Math.round(pinchRef.current.startZoom + Math.log2(pinchRef.current.scale || 1));
        pinchRef.current = { active: false, startDist: 0, startZoom: zoom, scale: 1 };
        setPinch(1);
        setZoom((z) => Math.min(MAX_Z, Math.max(MIN_Z, newZoom)));
        return;
      }
      if (!dragRef2.current.active) return;
      dragRef2.current.active = false;
      const { x, y } = deltaRef.current;
      if (Math.abs(x) > 4 || Math.abs(y) > 4) {
        const sc = Math.pow(2, zoom);
        const cw = project(center.lat, center.lng);
        const nx = cw.x - x / sc, ny = cw.y - y / sc;
        setCenter(worldToLatLng(nx, ny));
      }
      deltaRef.current = { x: 0, y: 0 }; setDelta({ x: 0, y: 0 });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [zoom, center.lat, center.lng]);

  const startDrag = (e) => {
    if (e.touches && e.touches.length === 2) {
      const a = e.touches[0], b = e.touches[1];
      pinchRef.current = {
        active: true,
        startDist: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY) || 1,
        startZoom: zoom,
        scale: 1,
      };
      dragRef2.current.active = false;
      return;
    }
    const pt = e.touches ? e.touches[0] : e;
    dragRef2.current = { active: true, sx: pt.clientX, sy: pt.clientY };
  };

  // Mouse-wheel / trackpad zoom, centred on the cursor
  const wheelAccum = useRef(0);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      wheelAccum.current += e.deltaY;
      if (Math.abs(wheelAccum.current) < 40) return;
      const dir = wheelAccum.current < 0 ? 1 : -1;
      wheelAccum.current = 0;
      const nz = Math.min(MAX_Z, Math.max(MIN_Z, zoom + dir));
      if (nz === zoom) return;
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left - rect.width / 2;
      const my = e.clientY - rect.top - rect.height / 2;
      const sc = Math.pow(2, zoom), nsc = Math.pow(2, nz);
      const cw = project(center.lat, center.lng);
      const cursorW = { x: cw.x + mx / sc, y: cw.y + my / sc };
      setCenter(worldToLatLng(cursorW.x - mx / nsc, cursorW.y - my / nsc));
      setZoom(nz);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoom, center.lat, center.lng]);


  useEffect(() => {
    const measure = () => { if (boxRef.current) setBox({ w: boxRef.current.offsetWidth, h: boxRef.current.offsetHeight }); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [loading]);

  const mq = mapQ.trim().toLowerCase();
  const visibleOffers = offers.filter((o) =>
    (!openOnly || isOpenNow(stores[o.vendor_id])) && (!favOnly || following.has(o.vendor_id)) &&
    (!mq || `${o.title || ""} ${o.store_name || ""}`.toLowerCase().includes(mq))
  );

  const reqW = Math.min(640, Math.round(box.w));
  const reqH = Math.min(640, Math.round(box.h));
  const staticSrc = GKEY
    ? `https://maps.googleapis.com/maps/api/staticmap?center=${center.lat},${center.lng}&zoom=${zoom}&size=${reqW}x${reqH}&scale=2&language=it&region=IT` +
      `&markers=${encodeURIComponent(`color:0x2563EB|size:small|${loc.lat},${loc.lng}`)}&key=${GKEY}`
    : null;

  const scale = Math.pow(2, zoom);
  const centerW = project(center.lat, center.lng);
  const pixelFor = (lat, lng) => { const w = project(lat, lng); return { left: (w.x - centerW.x) * scale + box.w / 2, top: (w.y - centerW.y) * scale + box.h / 2 }; };

  const clusters = [];
  visibleOffers.forEach((o) => {
    const p = pixelFor(o.lat, o.lng);
    if (p.left < -40 || p.top < -60 || p.left > box.w + 40 || p.top > box.h + 60) return;
    let placed = false;
    for (const c of clusters) {
      if (Math.hypot(c.left - p.left, c.top - p.top) < 46) {
        c.items.push(o); c.left = (c.left * (c.items.length - 1) + p.left) / c.items.length; c.top = (c.top * (c.items.length - 1) + p.top) / c.items.length; placed = true; break;
      }
    }
    if (!placed) clusters.push({ left: p.left, top: p.top, items: [o] });
  });

  const zoomIn = () => setZoom((z) => Math.min(MAX_Z, z + 1));
  const zoomOut = () => setZoom((z) => Math.max(MIN_Z, z - 1));
  const recenter = () => { setCenter({ lat: loc.lat, lng: loc.lng }); setZoom(14); requestGeo(); };
  const toggleCat = (id) => setSelCats((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const openPreview = (o) => {
    setActive(o);
    const cached = stores[o.vendor_id];
    setPreview(cached || { vendor_id: o.vendor_id, store_name: o.store_name });
    if (!cached) {
      api.get(`/stores/${o.vendor_id}`).then((x) => {
        const s = x.data.store;
        if (s) {
          setStores((prev) => ({ ...prev, [s.vendor_id]: s }));
          setPreview((p) => (p && p.vendor_id === s.vendor_id ? s : p));
        }
      }).catch(() => {});
    }
  };

  return (
    <div className="-mx-4 -my-4 relative overflow-hidden" style={{ height: "calc(100vh - 64px - 64px)" }} ref={boxRef} data-testid="offers-map">
      {loading && <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/60"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>}

      <div
        className="absolute inset-0"
        style={{ transform: `translate(${delta.x}px, ${delta.y}px) scale(${pinch})`, transformOrigin: "center center", touchAction: "none", cursor: dragRef2.current.active ? "grabbing" : "grab" }}
        onPointerDown={startDrag}
        onTouchStart={startDrag}
        data-testid="map-pan-layer"
      >
      {staticSrc ? (
        <img src={staticSrc} alt="Mappa delle offerte" draggable={false} className="absolute inset-0 w-full h-full object-cover select-none" data-testid="static-google-map" />
      ) : (
        <div className="absolute inset-0 bg-slate-100 flex items-center justify-center text-slate-400">Mappa non disponibile</div>
      )}

      {!loading && box.w > 0 && clusters.map((c, i) => {
        if (c.items.length === 1) {
          const o = c.items[0];
          const isActive = active?.offer_id === o.offer_id;
          const fav = following.has(o.vendor_id);
          return (
            <button key={o.offer_id} data-testid={`map-marker-${o.offer_id}`} onClick={() => openPreview(o)}
              style={{ left: c.left, top: c.top }}
              className={`absolute -translate-x-1/2 -translate-y-full z-[400] ${isActive ? "scale-110 z-[450]" : ""} transition-transform`}>
              <span className={`inline-flex items-center gap-1 text-white font-extrabold text-xs px-2 py-1 rounded-lg shadow-lg whitespace-nowrap ${fav ? "bg-amber-500 ring-2 ring-white" : "kn-gradient"}`}>
                {fav && <Heart className="h-3 w-3 fill-white" data-testid={`map-fav-${o.offer_id}`} />}{discountLabel(o)}
              </span>
            </button>
          );
        }
        const anyFav = c.items.some((o) => following.has(o.vendor_id));
        return (
          <button key={`cl-${i}`} data-testid={`map-cluster-${i}`} onClick={zoomIn} style={{ left: c.left, top: c.top }}
            className={`absolute -translate-x-1/2 -translate-y-1/2 z-[400] h-11 w-11 rounded-full text-white font-extrabold shadow-lg flex items-center justify-center border-2 border-white ${anyFav ? "bg-amber-500" : "kn-gradient"}`}>
            {c.items.length}
          </button>
        );
      })}
      </div>


      {/* Filters + open-now toggle */}
      <div className="absolute top-16 left-0 right-0 z-[600] px-3 space-y-2">
        <button
          data-testid="map-location-button"
          onClick={openLocation}
          className="inline-flex items-center gap-1.5 bg-white shadow-sm rounded-full pl-3 pr-3 h-9 text-xs font-bold text-slate-700 max-w-full"
        >
          <MapPin className="h-3.5 w-3.5 text-[#E63946] shrink-0" />
          <span className="truncate max-w-[180px]">{loc.label} · {loc.radius}km</span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        </button>
        <div className="relative" data-testid="map-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={mapQ} onChange={(e) => setMapQ(e.target.value)} data-testid="map-search-input"
            placeholder="Cerca offerta o negozio sulla mappa…" className="h-11 pl-9 pr-9 rounded-full bg-white shadow-sm border-0" />
          {mapQ && (
            <button onClick={() => setMapQ("")} data-testid="map-search-clear" className="absolute right-2.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center"><X className="h-3.5 w-3.5 text-slate-500" /></button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="map-filters">
          {cats.filter((m) => m.id !== "altro").map((m) => {
            const activeInMacro = (m.tags || []).some((t) => selCats.includes(t.id));
            const nSel = (m.tags || []).filter((t) => selCats.includes(t.id)).length;
            const isOpen = expandedMacro === m.id;
            return (
              <button key={m.id} data-testid={`map-filter-${m.id}`} onClick={() => setExpandedMacro(isOpen ? null : m.id)}
                className={`shrink-0 inline-flex items-center gap-1 px-3 h-9 rounded-full text-xs font-bold whitespace-nowrap shadow-sm transition-colors ${activeInMacro ? "kn-gradient text-white" : "bg-white text-slate-600"}`}>
                {m.label}
                {nSel > 0 && <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-white/30 text-[10px]">{nSel}</span>}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
            );
          })}
        </div>
        {expandedMacro && (() => {
          const m = cats.find((x) => x.id === expandedMacro);
          if (!m) return null;
          return (
            <div className="flex flex-wrap gap-2" data-testid={`map-subcats-${m.id}`}>
              {(m.tags || []).map((t) => (
                <button key={t.id} data-testid={`map-subfilter-${t.id}`} onClick={() => toggleCat(t.id)}
                  className={`shrink-0 px-3 h-8 rounded-full text-xs font-semibold whitespace-nowrap shadow-sm transition-colors ${selCats.includes(t.id) ? "bg-[#FFE066] text-slate-900 border border-[#FFD84A]" : "bg-white text-slate-600"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          );
        })()}
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-2 bg-white shadow-sm rounded-full pl-3 pr-2 h-9" data-testid="open-now-toggle">
            <span className="text-xs font-bold text-slate-600">Solo aperti ora</span>
            <Switch checked={openOnly} onCheckedChange={setOpenOnly} data-testid="open-now-switch" className="scale-90" />
          </div>
          {client && (
            <div className="inline-flex items-center gap-2 bg-white shadow-sm rounded-full pl-3 pr-2 h-9" data-testid="fav-only-toggle">
              <span className="text-xs font-bold text-slate-600 inline-flex items-center gap-1"><Heart className="h-3 w-3 text-amber-500 fill-amber-500" /> Che seguo</span>
              <Switch checked={favOnly} onCheckedChange={setFavOnly} data-testid="fav-only-switch" className="scale-90" />
            </div>
          )}
        </div>
      </div>

      <button data-testid="map-to-list" onClick={() => navigate("/")} className="absolute top-3 right-3 z-[600] h-11 px-4 rounded-full bg-white shadow-lg flex items-center gap-2 font-bold text-slate-700">
        <List className="h-4 w-4" /> Elenco
      </button>
      <div className="absolute top-3 left-3 z-[600] h-11 px-4 rounded-full bg-slate-900 text-white shadow-lg flex items-center gap-2 font-semibold text-sm">
        <MapPin className="h-4 w-4 text-[#FFB3BA]" /> {visibleOffers.length} offerte
      </div>

      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-[600] flex flex-col gap-2">
        <button data-testid="map-zoom-in" onClick={zoomIn} disabled={zoom >= MAX_Z} className="h-11 w-11 rounded-xl bg-white shadow-lg flex items-center justify-center text-slate-700 hover:bg-slate-50 disabled:opacity-40"><Plus className="h-5 w-5" /></button>
        <button data-testid="map-zoom-out" onClick={zoomOut} disabled={zoom <= MIN_Z} className="h-11 w-11 rounded-xl bg-white shadow-lg flex items-center justify-center text-slate-700 hover:bg-slate-50 disabled:opacity-40"><Minus className="h-5 w-5" /></button>
        <button data-testid="map-recenter" onClick={recenter} className="h-11 w-11 rounded-xl kn-gradient text-white shadow-lg flex items-center justify-center">
          {locating ? <Loader2 className="h-5 w-5 animate-spin" /> : <LocateFixed className="h-5 w-5" />}
        </button>
      </div>

      {/* Store preview card */}
      {preview && (
        <div className="absolute bottom-3 inset-x-3 z-[650] kn-card p-3 kn-pop" data-testid="store-preview">
          <button onClick={() => setPreview(null)} className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-white shadow flex items-center justify-center" data-testid="preview-close"><X className="h-4 w-4 text-slate-500" /></button>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
              {preview.logo ? <img src={preview.logo} alt="" className="h-full w-full object-cover" /> : <StoreIcon className="h-6 w-6 text-slate-400" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-slate-800 truncate flex items-center gap-1.5">{preview.store_name}{following.has(preview.vendor_id) && <Heart className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />}</p>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                {preview.google_rating && <span className="inline-flex items-center gap-0.5 text-amber-600 font-bold"><Star className="h-3 w-3 fill-amber-500 text-amber-500" />{preview.google_rating}</span>}
                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{todayHours(preview)}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button onClick={() => navigate(`/store/${preview.vendor_id}`)} data-testid="preview-store-btn" className="h-10 rounded-xl kn-gradient text-white font-bold text-sm">Vedi negozio</button>
            {preview.lat && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${preview.lat},${preview.lng}`} target="_blank" rel="noreferrer" data-testid="preview-directions" className="h-10 rounded-xl border border-slate-200 flex items-center justify-center gap-1.5 font-bold text-slate-700 text-sm">
                <Navigation className="h-4 w-4" /> Indicazioni
              </a>
            )}
          </div>
        </div>
      )}

      {/* Bottom offer strip (hidden while a preview is open) */}
      {!loading && !preview && visibleOffers.length > 0 && (
        <div className="absolute bottom-3 inset-x-0 z-[600] px-3">
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
            {(active ? [active, ...visibleOffers.filter((o) => o.offer_id !== active.offer_id)] : visibleOffers).map((o) => (
              <button key={o.offer_id} data-testid={`map-card-${o.offer_id}`} onClick={() => navigate(`/offer/${o.offer_id}`)} className="shrink-0 w-64 kn-card p-3 flex items-center gap-3 text-left kn-pop">
                <div className="h-12 w-12 rounded-xl kn-gradient text-white flex items-center justify-center font-extrabold text-sm shrink-0">{discountLabel(o)}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-800 text-sm truncate">{o.title}</p>
                  <p className="text-xs text-slate-400 truncate flex items-center gap-1"><Store className="h-3 w-3" /> {o.store_name}{o.distance_km != null && <span>· {distanceLabel(o.distance_km)}</span>}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {!loading && visibleOffers.length === 0 && (
        <div className="absolute bottom-3 inset-x-3 z-[600] kn-card p-4 text-center text-slate-500 text-sm">{favOnly ? "Nessuna offerta dai negozi che segui in zona." : openOnly ? "Nessun negozio aperto ora con questi filtri." : "Nessuna offerta con questi filtri in zona."}</div>
      )}
    </div>
  );
}
